import { useState, useEffect } from "react";
import { API, authFetch, useAuth } from "@/App";
import { toast } from "sonner";
import {
  UserCog, FileText, Building2, Phone, Mail, MapPin, Landmark,
  CheckCircle, XCircle, PauseCircle, FileImage, Download,
  AlertCircle,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ApprovalActionDialog, canApprove } from "@/components/ApprovalActionDialog";
import { RegionSwitcher } from "@/components/RegionSwitcher";
import { useRegionFilter } from "@/hooks/useRegionFilter";

const STATUS_TABS = [
  { key: "PENDING",  label: "Pending Review", color: "amber"  },
  { key: "ON_HOLD",  label: "On Hold",        color: "blue"   },
  { key: "APPROVED", label: "Approved",       color: "green"  },
  { key: "REJECTED", label: "Rejected",       color: "red"    },
];

const DOC_FIELDS = [
  ["profile_photo_path",  "Profile Photo",   FileImage],
  ["pan_card_path",       "PAN Card",        FileText],
  ["gst_certificate_path","GST Certificate", FileText],
  ["aadhar_card_path",    "Aadhar Card",     FileText],
  ["photo_id_path",       "Photo ID",        FileImage],
  ["letterhead_path",     "Letterhead",      FileText],
  ["cancelled_cheque_path","Cancelled Cheque", FileText],
];

const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

const OnboardingPage = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const canDecide = canApprove(user);
  const { activeRegion, setActiveRegion, filterByRegion } = useRegionFilter();

  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("PENDING");
  const [selected, setSelected] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(null);

  const fetchData = async () => {
    try {
      const res = await authFetch(`${API}/parties?limit=500`);
      const data = await res.json();
      if (data.success) setParties(data.data || []);
    } catch (e) {
      toast.error("Failed to load onboarding queue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const regionParties = filterByRegion(parties, (p) => p.region_id);
  const counts = STATUS_TABS.reduce((acc, t) => {
    acc[t.key] = regionParties.filter((p) => (p.approval_status || "PENDING") === t.key).length;
    return acc;
  }, {});
  const visibleParties = regionParties.filter(
    (p) => (p.approval_status || "PENDING") === statusTab
  );

  const handleApprovalSuccess = () => {
    setApprovalDialog(null);
    setSelected(null);
    fetchData();
  };

  return (
    <div className="space-y-6" data-testid="onboarding-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 font-['Sora']">Business Associate Onboarding</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            Review and approve new dealer / client applications. Pending submissions need your action.
          </p>
        </div>
        <RegionSwitcher
          active={activeRegion}
          onChange={setActiveRegion}
          isSuperAdmin={isSuperAdmin}
          dataTestId="onboarding-region-switcher"
        />
      </div>

      {/* Pending banner */}
      {counts.PENDING > 0 && (
        <Card className="p-4 border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/40 dark:border-amber-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center shrink-0">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-amber-900 dark:text-amber-100 text-base">
                {counts.PENDING} application{counts.PENDING > 1 ? "s" : ""} awaiting your review
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-200">Open each card to inspect form data + uploaded documents, then approve, hold, or reject.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="onboarding-status-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatusTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusTab === t.key
                ? "bg-[#8B1A1A] text-white shadow"
                : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
            data-testid={`onboarding-tab-${t.key}`}
          >
            {t.label} ({counts[t.key] || 0})
          </button>
        ))}
      </div>

      {/* Card grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-8 h-8 spinner" /></div>
      ) : visibleParties.length === 0 ? (
        <Card className="p-12 text-center">
          <UserCog className="w-12 h-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
          <p className="text-lg font-medium text-slate-700 dark:text-slate-200">No applications in this status</p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">When a new business associate form is submitted, it will appear here.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="onboarding-card-grid">
          {visibleParties.map((p) => (
            <Card
              key={p.party_id}
              className="p-4 hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-[#8B1A1A]"
              onClick={() => setSelected(p)}
              data-testid={`onboarding-card-${p.party_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {p.firm_name || p.contact_person || "Unnamed"}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">{p.party_id}</p>
                </div>
                <Badge variant="outline" className="shrink-0">
                  {p.party_type || "DEALER"}
                </Badge>
              </div>
              <div className="space-y-1.5 text-sm">
                {p.contact_person && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <UserCog className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{p.contact_person}</span>
                  </div>
                )}
                {p.mobile_number && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <Phone className="w-3.5 h-3.5 shrink-0" />
                    <span>{p.mobile_number}</span>
                  </div>
                )}
                {p.region_id && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <span>{p.region_id === "GJ" ? "Gujarat" : p.region_id === "RJ" ? "Rajasthan" : p.region_id} {p.district ? `· ${p.district}` : ""}</span>
                  </div>
                )}
                {p.gstin && (
                  <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <Building2 className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-mono text-xs truncate">{p.gstin}</span>
                  </div>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
                <span>{p.created_at ? new Date(p.created_at).toLocaleDateString("en-IN") : "—"}</span>
                <span>{(p.shop_photos?.length || 0) + DOC_FIELDS.filter(([k]) => p[k]).length} docs</span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="onboarding-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Sora']">{selected.firm_name || selected.contact_person}</DialogTitle>
                <p className="text-sm text-slate-500 font-mono">{selected.party_id}</p>
              </DialogHeader>

              <div className="space-y-6 mt-2">
                {/* Identity */}
                <Section title="Business Identity">
                  <Field label="Firm Name" value={selected.firm_name} />
                  <Field label="Party Type" value={selected.party_type} />
                  <Field label="Entity Type" value={selected.entity_type} />
                  <Field label="Year of Establishment" value={selected.year_of_establishment} />
                  <Field label="Category" value={selected.category} />
                  <Field label="Client Category" value={selected.client_category} />
                </Section>

                {/* Contact */}
                <Section title="Contact">
                  <Field label="Contact Person" value={selected.contact_person} />
                  <Field label="Father / Husband" value={selected.father_husband_name} />
                  <Field label="Mobile" value={selected.mobile_number} icon={Phone} />
                  <Field label="Alternate" value={selected.alternate_mobile} />
                  <Field label="Email" value={selected.email} icon={Mail} />
                  <Field label="Telephone (Residence)" value={selected.telephone_residence} />
                </Section>

                {/* Tax & Address */}
                <Section title="Tax & Address">
                  <Field label="GSTIN" value={selected.gstin} mono />
                  <Field label="PAN" value={selected.pan} mono />
                  <Field label="Owner Aadhar" value={selected.owner_aadhar_number} mono />
                  <Field label="State" value={selected.state} />
                  <Field label="District" value={selected.district} />
                  <Field label="Taluka" value={selected.taluka} />
                  <Field label="Region" value={selected.region_id} />
                  <Field label="Area" value={selected.area} />
                  <Field label="Address" value={selected.address} full icon={MapPin} />
                  <Field label="Site Address" value={selected.site_address} full />
                </Section>

                {/* Banking */}
                <Section title="Banking">
                  <Field label="Bank Name" value={selected.bank_name} icon={Landmark} />
                  <Field label="Account Type" value={selected.bank_account_type} />
                  <Field label="Account Number" value={selected.bank_account_number} mono />
                  <Field label="IFSC" value={selected.ifsc_code} mono />
                  <Field label="Branch" value={selected.bank_branch} />
                  <Field label="Signatory" value={selected.bank_signatory_name} />
                </Section>

                {/* Commercials */}
                <Section title="Commercials">
                  <Field label="Credit Limit" value={fmtINR(selected.credit_limit)} />
                  <Field label="Outstanding" value={fmtINR(selected.outstanding_balance)} />
                  <Field label="Loyalty Points" value={selected.loyalty_points} />
                  <Field label="Declaration Accepted" value={selected.declaration_accepted ? "Yes" : "No"} />
                  <Field label="Declaration Date" value={selected.declaration_date} />
                </Section>

                {/* Documents */}
                <Section title="Documents" full>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 col-span-full">
                    {DOC_FIELDS.map(([key, label, Icon]) => {
                      const path = selected[key];
                      return (
                        <div
                          key={key}
                          className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${
                            path
                              ? "border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800"
                              : "border-slate-200 bg-slate-50 dark:bg-slate-800 dark:border-slate-700"
                          }`}
                        >
                          <Icon className={`w-4 h-4 ${path ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400"}`} />
                          <span className={`flex-1 truncate ${path ? "text-emerald-800 dark:text-emerald-200" : "text-slate-500"}`}>{label}</span>
                          {path && (
                            <a
                              href={path}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs font-medium text-[#8B1A1A] hover:underline flex items-center gap-1"
                              data-testid={`doc-link-${key}`}
                            >
                              View <Download className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(selected.shop_photos || []).length > 0 && (
                    <div className="col-span-full">
                      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider">Shop / Site Photos</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {selected.shop_photos.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                            <img src={url} alt={`Shop ${i + 1}`} className="w-full aspect-square object-cover rounded-lg border border-slate-200 dark:border-slate-700" />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </Section>

                {/* Approval History */}
                {Array.isArray(selected.approval_history) && selected.approval_history.length > 0 && (
                  <Section title="Approval History" full>
                    <div className="col-span-full space-y-2">
                      {selected.approval_history.slice().reverse().map((h, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm border-l-2 border-slate-300 dark:border-slate-700 pl-3 py-1">
                          <Badge variant="outline" className="shrink-0">{h.action}</Badge>
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-700 dark:text-slate-200">{h.by} <span className="text-slate-400">·</span> {h.by_role}</p>
                            {h.notes && <p className="text-slate-500 dark:text-slate-400 text-xs">{h.notes}</p>}
                            <p className="text-xs text-slate-400 dark:text-slate-500">{h.timestamp ? new Date(h.timestamp).toLocaleString("en-IN") : ""}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}
              </div>

              {/* Action bar */}
              {canDecide && selected.approval_status !== "APPROVED" && selected.approval_status !== "REJECTED" && (
                <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <Button
                    variant="outline"
                    onClick={() => setApprovalDialog({ entity: selected, action: "REJECT" })}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="onboarding-reject-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setApprovalDialog({ entity: selected, action: "HOLD" })}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="onboarding-hold-btn"
                  >
                    <PauseCircle className="w-4 h-4 mr-2" /> Hold
                  </Button>
                  <Button
                    onClick={() => setApprovalDialog({ entity: selected, action: "APPROVE" })}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="onboarding-approve-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" /> Approve
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog (shared component) */}
      {approvalDialog && (
        <ApprovalActionDialog
          open
          onClose={() => setApprovalDialog(null)}
          entity="dealer"
          action={approvalDialog.action}
          entityId={approvalDialog.entity.party_id}
          entityLabel={approvalDialog.entity.firm_name || approvalDialog.entity.contact_person}
          endpointBase="/parties"
          tags={[
            "Documents Verified", "GST Verified", "PAN Verified", "Bank Details OK",
            "Site Visit Done", "Credit Worthy", "Reference Confirmed",
            "Documents Incomplete", "GST Mismatch", "PAN Issue", "Bank Details Pending",
            "Site Photos Missing", "Declaration Not Signed", "Mobile Unreachable",
            "Address Mismatch", "Duplicate Application", "Awaiting Field Visit",
            "Other",
          ]}
          onSuccess={handleApprovalSuccess}
        />
      )}
    </div>
  );
};

const Section = ({ title, children, full }) => (
  <div>
    <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">{title}</h4>
    <div className={`grid ${full ? "" : "grid-cols-1 md:grid-cols-2"} gap-x-4 gap-y-2 bg-slate-50 dark:bg-slate-900/40 p-3 rounded-lg`}>
      {children}
    </div>
  </div>
);

const Field = ({ label, value, icon: Icon, mono, full }) => (
  <div className={full ? "col-span-full" : ""}>
    <p className="text-[11px] uppercase text-slate-400 dark:text-slate-500 font-semibold tracking-wider">{label}</p>
    <p className={`text-sm flex items-center gap-2 ${mono ? "font-mono" : ""} text-slate-800 dark:text-slate-100`}>
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      {value || <span className="text-slate-300">—</span>}
    </p>
  </div>
);

export default OnboardingPage;
